import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { ChevronDown, Settings, Calculator, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { HelpCircle } from "lucide-react"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  USE_CASES,
  MODEL_FAMILIES,
  type CalculationRequest,
  type CalculationResult,
} from "@/types/calculator";
import { useToast } from "@/hooks/use-toast";
import { API_BASE_URL } from "@/config";
import { buildSizingReport, reportFileName } from "@/lib/pdfReport";

// Start loading jsPDF as soon as this module is evaluated (app load), not on the
// export click. Firefox drops the user-activation flag across `await`, so an
// `await import("jspdf")` inside the click handler can get the follow-up download
// blocked as "not user-initiated". By click time this promise is already resolved,
// so awaiting it is just a microtask and the click's activation is preserved.
const jsPdfModule = import("jspdf");

interface InputPanelProps {
  onCalculationStart: () => void;
  onCalculationComplete: (result: CalculationResult) => void;
  onCalculationError: () => void;
  isCalculating: boolean;
  result: CalculationResult | null;
}

export function InputPanel({
  onCalculationStart,
  onCalculationComplete,
  onCalculationError,
  isCalculating,
  result,
}: InputPanelProps) {
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [fetchingModel, setFetchingModel] = useState(false);
  const { toast } = useToast();

  const [gpuOptions, setGpuOptions] = useState<Record<string, string>>({});
  const [currentGpuSupportsNvlink, setCurrentGpuSupportsNvlink] = useState(false);
  const [manualGpuCount, setManualGpuCount] = useState(false);

  // Fetch GPU specs when component mounts
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/gpu-specs`)
      .then((res) => res.json())
      .then((data) => {
        const formatted: Record<string, string> = {};

        if (Array.isArray(data)) {
          // For array response
          data.forEach((gpu: { key?: string; name?: string }) => {
            if (gpu.key && gpu.name) formatted[gpu.key] = gpu.name;
          });
        } else if (typeof data === "object") {
          // For object response (like your GPU_SPECS)
          Object.entries(data).forEach(([key, gpu]) => {
            if (gpu && typeof gpu === "object" && "name" in gpu) {
              formatted[key] = String((gpu as { name: unknown }).name); // only take the name
            }
          });
        }

        setGpuOptions(formatted);
      })
      .catch((err) => {
        console.error("Failed to fetch GPU specs:", err);
        setGpuOptions({});
      });
  }, []);

  // Function to update NVLink support when GPU changes
  const updateNvlinkSupport = (gpuType: string) => {
    if (!gpuType) {
      setCurrentGpuSupportsNvlink(false);
      form.setValue("enableNvlink", false);
      return;
    }

    fetch(`${API_BASE_URL}/api/gpu-specs/${gpuType}`)
      .then((res) => res.json())
      .then((gpuSpec) => {
        const supportsNvlink = gpuSpec.nvlink === true;
        setCurrentGpuSupportsNvlink(supportsNvlink);
        // If GPU supports NVLink, enable it by default; otherwise disable it
        form.setValue("enableNvlink", supportsNvlink);
      })
      .catch((err) => {
        console.error("Failed to fetch GPU spec:", err);
        setCurrentGpuSupportsNvlink(false);
        form.setValue("enableNvlink", false);
      });
  };

  const form = useForm<CalculationRequest>({
    defaultValues: {
      useCase: "",
      gpuType: "",
      enableRAG: false,
      highAvailability: false,
      enableNvlink: false,
      precision: "FP16",
      modelFamily: "",
      concurrentUsers: 100,
      requestsPerUserPerMinute: 2,
      inputTokens: 512,
      outputTokens: 256,
      gpuOverhead: 20,
      gpuCount: 1,
      customModelName: "",
      params: 0,
      layers: 0,
      hidden_size: 0,
      kv_heads: 0,
      heads: 0,
      is_moe: false,
      active_params: 0
    },
  });

  const handleUseCaseChange = (useCase: string) => {
    form.setValue("useCase", useCase);
    const useCaseConfig = USE_CASES[useCase as keyof typeof USE_CASES];
    if (useCaseConfig) {
      fetch(`${API_BASE_URL}/api/use-cases`)
        .then((response) => response.json())
        .then((configs) => {
          const config = configs[useCase];
          const currentGpuType = form.getValues("gpuType");
          const updatedValues = {
            ...form.getValues(),
            useCase,
            gpuType:currentGpuType || "",
            modelFamily: config.model,
            precision: config.precision,
            concurrentUsers: config.concurrent_users,
            requestsPerUserPerMinute: config.requests_per_user_per_min ?? 2,
            inputTokens: config.input_tokens,
            outputTokens: config.output_tokens,
            enableRAG: config.enable_rag ?? false,
            highAvailability: config.high_availability ?? false,
            gpuOverhead: config.gpu_overhead,
            customModelName: config.custom_model_name ?? "",
            params: config.params ?? 1,    // the rest takes from model config
            layers: config.layers ?? 1 ,
            hidden_size: config.hidden_size ?? 1,
            kv_heads: config.kv_heads ?? 1,
            heads: config.heads ?? 1,
            is_moe: config.is_moe ?? false,
            active_params: config.active_params ?? 0
            };

            // Update form state so submission matches backend config
            form.reset(updatedValues);
          }
        )
        .catch((error) =>
          console.error("Error fetching use case config:", error)
        );
    }
  };

  // Pull architecture (params/layers/heads/MoE) from HuggingFace and fill the
  // custom-model fields. Runs when the user presses Enter or leaves the field.
  const fetchCustomModelInfo = async () => {
    if (form.getValues("modelFamily") !== "custom") return;
    const name = (form.getValues("customModelName") || "").trim();
    if (!name || name.toLowerCase() === "custom" || fetchingModel) return;

    setFetchingModel(true);
    try {
      // Sent as a path ("org/name"): underscores in model names must survive.
      const modelPath = name.split("/").map(encodeURIComponent).join("/");
      const res = await fetch(`${API_BASE_URL}/api/model-info/${modelPath}`);
      const body = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(body?.detail || `HuggingFace lookup failed (${res.status})`);
      }
      const modelInfo = body ?? {};
      form.reset({
        ...form.getValues(),
        params: modelInfo.params ?? form.getValues("params"),
        layers: modelInfo.layers ?? form.getValues("layers"),
        hidden_size: modelInfo.hidden_size ?? form.getValues("hidden_size"),
        kv_heads: modelInfo.kv_heads ?? form.getValues("kv_heads"),
        heads: modelInfo.heads ?? form.getValues("heads"),
        is_moe: modelInfo.is_moe ?? form.getValues("is_moe"),
        active_params: modelInfo.active_params ?? form.getValues("active_params"),
      });
      toast({
        title: "Model config loaded",
        description: `Filled fields from ${name}.`,
        duration: 2000,
        className: "bg-white text-black-600 border-green-700 opacity-90",
      });
    } catch (err) {
      console.error("Error fetching custom model info:", err);
      toast({
        title: "Couldn't load model config",
        description: err instanceof Error ? err.message : "See console for details.",
        variant: "destructive",
        duration: 4000,
        className: "bg-white text-black-600 border-red-700 opacity-90",
      });
    }
    setFetchingModel(false);
  };

  const handleCustomModelEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault(); // stop form submit
      fetchCustomModelInfo();
    }
  };

  const onSubmit = async (data: CalculationRequest) => {
    console.log("Submitting data:", data);
    if (data.modelFamily === "custom" && !data.customModelName) {
      data.customModelName = "Custom";
    }
    console.log("Submitting:", data);
    if (!data.useCase) {
      toast({ title: "Error", description: "Please select a use case", variant: "destructive", duration: 1500, className: "bg-white text-black-600 border-red-700 opacity-60" });
      return;
    }
    if (!data.gpuType) {
      toast({ title: "Error", description: "Please select a GPU type", variant: "destructive", duration: 1500, className: "bg-white text-black-600 border-red-700 opacity-60" });
      return;
    }

    // Manual GPU count: send the chosen number; otherwise null = auto-size from VRAM.
    const chosenGpus = Number(data.gpuCount);
    data.gpuCount = manualGpuCount && chosenGpus > 0 ? chosenGpus : null;

    onCalculationStart();
    try {
      const response = await fetch(`${API_BASE_URL}/api/calculate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      onCalculationComplete(result);

      toast({
        title: "Calculation Complete",
        description: "GPU requirements have been calculated successfully",
        duration: 2000,
        className: "bg-white text-black-600 border-green-700 opacity-90",
      });
    } catch (error) {
      console.error("Calculation error:", error);
      onCalculationError();
      toast({ title: "Calculation Failed", description: "There was an error calculating the requirements. Please try again.", variant: "destructive", duration: 1500, className: "bg-white text-black-600 border-red-700 opacity-90" });
    }
  };

  async function handleExport() {
    // Button stays focusable via aria-disabled (not the disabled attr), so guard here.
    if (!result || exporting) return;
    setExporting(true);
    try {
      const { jsPDF } = await jsPdfModule;
      const doc = buildSizingReport(jsPDF, result);

      // Don't use doc.save(): its bundled saver clicks a detached <a>, which
      // Firefox ignores. Build the anchor, attach it to the DOM, click, remove.
      const blobUrl = URL.createObjectURL(doc.output("blob"));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = reportFileName();
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    } catch (err) {
      console.error("PDF export failed:", err);
      toast({
        title: "PDF export failed",
        description: err instanceof Error ? err.message : "Could not generate the report.",
        variant: "destructive",
        duration: 4000,
        className: "bg-white text-black-600 border-red-700 opacity-90",
      });
    }
    setExporting(false);
  }

  const labelCls = "text-sm font-bold text-gray-700 dark:text-gray-300";
  const inputCls = "bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-lenovo-red focus:border-lenovo-red";
  const selectTriggerCls = "bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-lenovo-red focus:border-lenovo-red transition-all duration-300 hover:border-lenovo-blue hover:shadow-md hover:scale-[1.01]";
  const selectContentCls = "z-50 bg-white dark:bg-gray-800 shadow-xl border border-gray-200 dark:border-gray-700";
  const selectItemCls = "cursor-pointer text-gray-900 dark:text-gray-100 hover:bg-lenovo-red hover:text-white transition-all duration-200";

  return (
    <div className="space-y-6">
      <Card className="relative overflow-visible transition-all duration-300 hover:shadow-lg bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
        <CardContent className="p-6 overflow-visible">

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" className="absolute top-4 right-4 text-gray-500 dark:text-gray-400 hover:text-lenovo-blue">
                  <HelpCircle className="w-5 h-5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="left" className="max-w-xs bg-white dark:bg-gray-800 text-black dark:text-white border-gray-200 dark:border-gray-700 opacity-100">
                Select your usecase and parameters will be prefilled. For custom model use HuggingFace format e.g. "mistralai/Mixtral-8x7B-v0.1" and press Enter to load parameters.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-6 transition-colors duration-300 hover:text-lenovo-blue">
            LLM Sizing Calculator
          </h2>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && e.target instanceof HTMLInputElement) {
                  if (e.target.type !== "textarea") e.preventDefault();
                }
              }}
              className="space-y-6"
            >
              {/* Use Case */}
              <div className="group transition-all duration-300 hover:bg-gradient-to-r hover:from-lenovo-blue/5 hover:to-lenovo-red/5 p-4 rounded-lg -m-4 hover:shadow-md">
                <FormField control={form.control} name="useCase" render={({ field }) => (
                  <FormItem>
                    <FormLabel className={labelCls}>Use Case <span className="text-lenovo-red">*</span></FormLabel>
                    <Select onValueChange={(value) => { field.onChange(value); handleUseCaseChange(value); }} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger className={selectTriggerCls}><SelectValue placeholder="Select a use case..." /></SelectTrigger>
                      </FormControl>
                      <SelectContent className={selectContentCls}>
                        {Object.entries(USE_CASES).map(([key, value]) => (
                          <SelectItem key={key} value={key} className={selectItemCls}>{value}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )} />
              </div>

                {/* GPU Selection */}
                <div className="group transition-all duration-300 hover:bg-gradient-to-r hover:from-lenovo-red/5 hover:to-lenovo-blue/5 p-4 rounded-lg -m-4 hover:shadow-md">
                  <FormField
                    control={form.control}
                    name="gpuType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-bold text-gray-700 dark:text-gray-300 group-hover:text-lenovo-blue transition-colors duration-300">
                          GPU Type <span className="text-lenovo-red">*</span>
                        </FormLabel>
                        <Select
                          onValueChange={(value) => {
                            field.onChange(value);
                            updateNvlinkSupport(value);
                          }}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger className="focus:ring-lenovo-red focus:border-lenovo-red transition-all duration-300 hover:border-lenovo-blue hover:shadow-md hover:scale-[1.01]">
                              <SelectValue placeholder="Select a GPU..." />
                            </SelectTrigger>
                          </FormControl>

                          <SelectContent className="z-50 bg-white dark:bg-gray-800 shadow-xl border border-gray-200 dark:border-gray-700">
                            {Object.keys(gpuOptions).length === 0 ? (
                              <div className="p-2 text-gray-500">Loading GPUs...</div>
                            ) : (
                              Object.entries(gpuOptions).map(([key, name]) => (
                                <SelectItem
                                  key={key}
                                  value={key}
                                  className="cursor-pointer hover:bg-lenovo-red hover:text-white transition-all duration-200 hover:transform hover:scale-[1.02]"
                                >
                                  {name}
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                      </FormItem>
                    )}
                  />
                </div>

                {/* Enable RAG + High Availability + NVLink */}
                <div className="group transition-all duration-300 hover:bg-gradient-to-r hover:from-lenovo-red/5 hover:to-lenovo-blue/5 p-4 rounded-lg -m-4 hover:shadow-md">
                  <div className="flex flex-wrap gap-x-6 gap-y-4">
                    {/* Enable RAG */}
                    <FormField
                      control={form.control}
                      name="enableRAG"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              className="border-gray-300 text-lenovo-red focus:ring-lenovo-red transition-all duration-300 hover:border-lenovo-blue hover:scale-110"
                            />
                          </FormControl>
                          <FormLabel className="text-sm font-bold text-gray-700 dark:text-gray-300 group-hover:text-lenovo-blue transition-colors duration-300">
                            Enable RAG
                          </FormLabel>
                        </FormItem>
                      )}
                    />

                    {/* High Availability */}
                    <FormField
                      control={form.control}
                      name="highAvailability"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              className="border-gray-300 text-lenovo-red focus:ring-lenovo-red transition-all duration-300 hover:border-lenovo-blue hover:scale-110"
                            />
                          </FormControl>
                          <FormLabel className="text-sm font-bold text-gray-700 dark:text-gray-300 group-hover:text-lenovo-blue transition-colors duration-300">
                            High Availability
                          </FormLabel>
                        </FormItem>
                      )}
                    />

                    {/* Enable NVLink */}
                    <FormField
                      control={form.control}
                      name="enableNvlink"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              disabled={!currentGpuSupportsNvlink}
                              className={`border-gray-300 text-lenovo-red focus:ring-lenovo-red transition-all duration-300 hover:border-lenovo-blue hover:scale-110 ${
                                !currentGpuSupportsNvlink ? 'opacity-50 cursor-not-allowed' : ''
                              }`}
                            />
                          </FormControl>
                          <FormLabel className={`text-sm font-bold transition-colors duration-300 ${
                            !currentGpuSupportsNvlink
                              ? 'text-gray-400 cursor-not-allowed'
                              : 'text-gray-700 dark:text-gray-300 group-hover:text-lenovo-blue'
                          }`}>
                            Enable NVLink
                          </FormLabel>
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                {/* Manual GPU count */}
                <div className="group transition-all duration-300 hover:bg-gradient-to-r hover:from-lenovo-blue/5 hover:to-lenovo-red/5 p-4 rounded-lg -m-4 hover:shadow-md">
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      checked={manualGpuCount}
                      onCheckedChange={(checked) => setManualGpuCount(checked === true)}
                      className="border-gray-300 text-lenovo-red focus:ring-lenovo-red transition-all duration-300 hover:border-lenovo-blue hover:scale-110"
                    />
                    <Label className="text-sm font-bold text-gray-700 dark:text-gray-300 group-hover:text-lenovo-blue transition-colors duration-300">
                      Set GPU count manually
                    </Label>
                  </div>
                  {manualGpuCount && (
                    <FormField
                      control={form.control}
                      name="gpuCount"
                      render={({ field }) => (
                        <FormItem className="mt-3">
                          <FormLabel className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Number of GPUs (Tensor Parallelism = N)
                          </FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              min={1}
                              {...field}
                              value={field.value ?? 1}
                              onChange={(e) => field.onChange(parseInt(e.target.value))}
                              className="focus:ring-lenovo-red focus:border-lenovo-red transition-all duration-300 hover:border-lenovo-blue"
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  )}
                  <p className="mt-2 text-xs text-gray-500">
                    When enabled, the app uses exactly this many GPUs instead of sizing from VRAM, and warns if they can't host the workload.
                  </p>
                </div>

              {/* Advanced Parameters */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <div className="group transition-all duration-300 hover:bg-gradient-to-r hover:from-lenovo-blue/5 hover:to-lenovo-red/5 p-4 rounded-lg -m-4 hover:shadow-md">
                  <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
                    <CollapsibleTrigger className="flex items-center justify-between w-full text-left focus:outline-none focus:ring-2 focus:ring-lenovo-red rounded-md p-2 -m-2 transition-all duration-300 hover:bg-lenovo-blue/10 hover:shadow-sm">
                      <div className="flex items-center space-x-2">
                        <Settings className="w-4 h-4 text-lenovo-blue transition-all duration-300 group-hover:text-lenovo-red group-hover:rotate-45" />
                        <span className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-lenovo-blue transition-colors duration-300">Advanced Parameters</span>
                      </div>
                      <ChevronDown className={`w-4 h-4 text-gray-400 dark:text-gray-500 transition-all duration-300 group-hover:text-lenovo-red ${advancedOpen ? "rotate-180" : ""}`} />
                    </CollapsibleTrigger>

                      <CollapsibleContent className="mt-4 space-y-4">
                        {/* Model Family */}
                        <div className="group/field transition-all duration-300 hover:bg-gradient-to-r hover:from-lenovo-blue/5 hover:to-transparent p-3 rounded-lg -m-3 hover:shadow-sm">
                            <FormField
                              control={form.control}
                              name="modelFamily"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover/field:text-lenovo-blue transition-colors duration-300">
                                    Model Family
                                  </FormLabel>
                                  <Select
                                    onValueChange={field.onChange}
                                    value={field.value}
                                  >
                                    <FormControl>
                                      <SelectTrigger className="focus:ring-lenovo-red focus:border-lenovo-red transition-all duration-300 hover:border-lenovo-blue hover:shadow-md hover:scale-[1.01]">
                                        <SelectValue placeholder="Select model family..." />
                                      </SelectTrigger>
                                    </FormControl>
                                    <SelectContent className="z-50 bg-white dark:bg-gray-800 shadow-xl border border-gray-200 dark:border-gray-700">
                                      {Object.entries(MODEL_FAMILIES).map(([key, value]) => (
                                        <SelectItem
                                          key={key}
                                          value={key}
                                          className="cursor-pointer hover:bg-lenovo-red hover:text-white transition-all duration-200 hover:transform hover:scale-[1.02]"
                                        >
                                          {value}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>

                                  {/* Extra inputs for custom model */}
                                  {form.watch("modelFamily") === "custom" && (
                                    <div className="mt-4 space-y-4">
                                      {/* Model Name */}
                                      <FormField
                                        control={form.control}
                                        name="customModelName"
                                        render={({ field }) => (
                                          <FormItem>
                                            <FormLabel>Custom Model Name</FormLabel>
                                            <FormControl>
                                              <Input
                                                placeholder="e.g. Qwen/Qwen2.5-7B-Instruct"
                                                {...field}
                                                onKeyDown={handleCustomModelEnter}
                                                onBlur={() => { field.onBlur(); fetchCustomModelInfo(); }}
                                              />
                                            </FormControl>
                                            <p className="text-xs text-gray-500 mt-1">
                                              {fetchingModel
                                                ? "Fetching config from HuggingFace…"
                                                : "Press Enter or click away to auto-fill from HuggingFace. Gated repos need HF_TOKEN set on the backend."}
                                            </p>
                                          </FormItem>
                                        )}
                                      />

                                      {/* Params */}
                                      <FormField
                                        control={form.control}
                                        name="params"
                                        render={({ field }) => (
                                          <FormItem>
                                            <FormLabel>Params</FormLabel>
                                            <FormControl>
                                              <Input placeholder="e.g. 7B" {...field} />
                                            </FormControl>
                                          </FormItem>
                                        )}
                                      />

                                      {/* Layers */}
                                      <FormField
                                        control={form.control}
                                        name="layers"
                                        render={({ field }) => (
                                          <FormItem>
                                            <FormLabel>Layers</FormLabel>
                                            <FormControl>
                                              <Input placeholder="Number of layers" {...field} />
                                            </FormControl>
                                          </FormItem>
                                        )}
                                      />

                                      {/* Hidden Size */}
                                      <FormField
                                        control={form.control}
                                        name="hidden_size"
                                        render={({ field }) => (
                                          <FormItem>
                                            <FormLabel>Hidden Size</FormLabel>
                                            <FormControl>
                                              <Input placeholder="Hidden size" {...field} />
                                            </FormControl>
                                          </FormItem>
                                        )}
                                      />

                                      {/* KV Heads */}
                                      <FormField
                                        control={form.control}
                                        name="kv_heads"
                                        render={({ field }) => (
                                          <FormItem>
                                            <FormLabel>KV Heads</FormLabel>
                                            <FormControl>
                                              <Input placeholder="Number of KV heads" {...field} />
                                            </FormControl>
                                          </FormItem>
                                        )}
                                      />

                                      {/* Heads */}
                                      <FormField
                                        control={form.control}
                                        name="heads"
                                        render={({ field }) => (
                                          <FormItem>
                                            <FormLabel>Heads</FormLabel>
                                            <FormControl>
                                              <Input placeholder="Number of heads" {...field} />
                                            </FormControl>
                                          </FormItem>
                                        )}
                                      />
                                      {/* is_moe */} 
                                      <FormField
                                        control={form.control}
                                        name="is_moe"
                                        render={({ field }) => (
                                          <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                                            <FormControl>
                                              <Checkbox
                                                checked={field.value}
                                                onCheckedChange={field.onChange}
                                                className="border-gray-300 text-lenovo-red focus:ring-lenovo-red transition-all duration-300 hover:border-lenovo-blue hover:scale-110"
                                              />
                                            </FormControl>
                                            <FormLabel className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-lenovo-blue transition-colors duration-300">
                                              Mixture of Experts
                                            </FormLabel>
                                          </FormItem>
                                        )}
                                      />
                                    </div>
                                  )}
                                </FormItem>
                              )}
                            />
                          </div>

                        {/* Precision */}
                        <div className="group/field transition-all duration-300 hover:bg-gradient-to-r hover:from-lenovo-red/5 hover:to-transparent p-3 rounded-lg -m-3 hover:shadow-sm">
                          <FormField
                            control={form.control}
                            name="precision"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover/field:text-lenovo-blue transition-colors duration-300">
                                  Precision
                                </FormLabel>
                                <Select
                                  onValueChange={field.onChange}
                                  value={field.value}
                                >
                                  <FormControl>
                                    <SelectTrigger className="focus:ring-lenovo-red focus:border-lenovo-red transition-all duration-300 hover:border-lenovo-blue hover:shadow-md hover:scale-[1.01]">
                                      <SelectValue />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent className="z-50 bg-white dark:bg-gray-800 shadow-xl border border-gray-200 dark:border-gray-700">
                                    {["FP16", "FP32", "BF16", "FP8", "INT8", "INT4/NF4"].map(
                                      (val) => (
                                        <SelectItem
                                          key={val}
                                          value={val}
                                          className="cursor-pointer hover:bg-lenovo-red hover:text-white transition-all duration-200 hover:transform hover:scale-[1.02]"
                                        >
                                          {val === "FP16"
                                            ? "FP16 (Half Precision)"
                                            : val === "FP32"
                                            ? "FP32 (Full Precision)"
                                            : val === "BF16"
                                            ? "BF16 (Brain Float)"
                                            : val === "FP8"
                                            ? "FP8 (Hopper/Blackwell)"
                                            : val === "INT8"
                                            ? "INT8 (Quantized)"
                                            : "INT4/NF4 (Highly Quantized)"}
                                        </SelectItem>
                                      )
                                    )}
                                  </SelectContent>
                                </Select>
                              </FormItem>
                            )}
                          />
                        </div>

                      {/* Tokens */}
                      <div className="grid grid-cols-2 gap-4">
                        <FormField control={form.control} name="inputTokens" render={({ field }) => (
                          <FormItem>
                            <FormLabel className={labelCls}>Input Tokens</FormLabel>
                            <FormControl><Input type="number" min="1" {...field} onChange={(e) => field.onChange(parseInt(e.target.value))} className={inputCls} /></FormControl>
                          </FormItem>
                        )} />
                        <FormField control={form.control} name="outputTokens" render={({ field }) => (
                          <FormItem>
                            <FormLabel className={labelCls}>Output Tokens</FormLabel>
                            <FormControl><Input type="number" min="1" {...field} onChange={(e) => field.onChange(parseInt(e.target.value))} className={inputCls} /></FormControl>
                          </FormItem>
                        )} />
                      </div>

                      <FormField control={form.control} name="concurrentUsers" render={({ field }) => (
                        <FormItem>
                          <FormLabel className={labelCls}>Concurrent Users</FormLabel>
                          <FormControl><Input type="number" min="1" {...field} onChange={(e) => field.onChange(parseInt(e.target.value))} className={inputCls} /></FormControl>
                        </FormItem>
                      )} />

                      <FormField control={form.control} name="requestsPerUserPerMinute" render={({ field }) => (
                        <FormItem>
                          <FormLabel className={labelCls}>Requests per User per Minute</FormLabel>
                          <FormControl><Input type="number" min={0} step="0.1" {...field} onChange={(e) => field.onChange(parseFloat(e.target.value))} className={inputCls} /></FormControl>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Sets how many requests are in flight for latency. 0 = every user always waiting (worst case).
                          </p>
                        </FormItem>
                      )} />

                      <FormField control={form.control} name="gpuOverhead" render={({ field }) => (
                        <FormItem>
                          <FormLabel className={labelCls}>GPU Overhead (%)</FormLabel>
                          <FormControl><Input type="number" min={0} max={95} {...field} onChange={(e) => field.onChange(parseFloat(e.target.value))} className={inputCls} /></FormControl>
                        </FormItem>
                      )} />
                    </CollapsibleContent>
                  </Collapsible>
                </div>
              </div>

              {/* Calculate Button */}
              <Button type="submit" disabled={isCalculating} className="w-full bg-lenovo-red hover:bg-lenovo-red text-white font-medium py-3 px-4 transition-all duration-300 focus:ring-2 focus:ring-lenovo-red focus:ring-offset-2 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]">
                {isCalculating ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />Calculating...</> : <><Calculator className="w-4 h-4 mr-2" />Calculate GPU Requirements</>}
              </Button>

              {/* Export */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <Button
                  type="button"
                  onClick={handleExport}
                  aria-disabled={!result || exporting}
                  aria-busy={exporting}
                  aria-describedby={!result ? "pdf-export-hint" : undefined}
                  className={`w-full bg-lenovo-red text-white font-medium py-2.5 transition-all duration-300 ${
                    !result || exporting
                      ? "opacity-40 cursor-not-allowed"
                      : "hover:opacity-90 hover:shadow-md"
                  }`}
                >
                  {exporting ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />Generating PDF...</> : <><Download className="w-4 h-4 mr-2" />Download PDF Report</>}
                </Button>
                {!result && <p id="pdf-export-hint" className="mt-2 text-xs text-gray-400 dark:text-gray-500 text-center">Run a calculation first to enable export</p>}
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}